import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Check, X } from 'lucide-react';
import { FinalizeWorkflowRequest, workflowApi } from '../services/api';
import './WorkflowUATModal.css';

interface WorkflowUATModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName: string;
  onComplete: () => void;
}

interface UATChecklistItem {
  id: string;
  label: string;
  description: string;
  checked: boolean;
}

const DEFAULT_CHECKLIST: UATChecklistItem[] = [
  {
    id: 'all_tests_passed',
    label: 'I confirm that all tests have passed',
    description: 'Output data matches expected results and calculations are correct. All formulas and transformations work as intended. Output files are in the correct format and structure.',
    checked: false,
  },
];

export const WorkflowUATModal: React.FC<WorkflowUATModalProps> = ({
  isOpen,
  onClose,
  workflowId,
  workflowName,
  onComplete,
}) => {
  const [testsPassed, setTestsPassed] = useState(false);
  const [testingNotes, setTestingNotes] = useState('');
  const [uatFile, setUatFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setUatFile(acceptedFiles[0]);
      }
    },
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleSubmit = async () => {
    if (!testsPassed) {
      setError('Please confirm that all tests have passed before submitting');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const uatResults: Record<string, any> = {
        all_tests_passed: {
          passed: testsPassed,
          notes: testingNotes || '',
          label: 'All Tests Passed',
        },
      };

      const data: FinalizeWorkflowRequest = {
        uat_results: uatResults,
        uat_summary: testingNotes || `UAT completed for ${workflowName}`,
      };

      await workflowApi.finalizeWithUAT(workflowId, data, uatFile || undefined);
      onComplete();
      onClose();
    } catch (err: any) {
      const errorData = err.response?.data;
      let errorMessage = 'Failed to complete UAT';
      
      if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        // Handle validation errors from FastAPI
        errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorData?.detail && typeof errorData.detail === 'object') {
        errorMessage = errorData.detail.msg || JSON.stringify(errorData.detail);
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container uat-modal">
        <div className="modal-header">
          <h2>User Acceptance Testing</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="uat-intro">
            <h3>Complete UAT for: {workflowName}</h3>
            <p>
              Please verify that the workflow produces accurate results and meets all requirements.
              Check each item below after testing.
            </p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="uat-checklist">
            <h4>Test Confirmation</h4>
            <div className="checklist-item">
              <div className="checklist-header">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={testsPassed}
                    onChange={(e) => setTestsPassed(e.target.checked)}
                  />
                  <span className={`checkbox-icon ${testsPassed ? 'checked' : ''}`}>
                    {testsPassed && <Check size={18} />}
                  </span>
                  <span className="checklist-label">{DEFAULT_CHECKLIST[0].label}</span>
                </label>
              </div>
              <p className="checklist-description">{DEFAULT_CHECKLIST[0].description}</p>
            </div>
          </div>

          <div className="uat-summary">
            <h4>Testing Notes</h4>
            <textarea
              value={testingNotes}
              onChange={(e) => setTestingNotes(e.target.value)}
              placeholder="Describe your testing process, any issues found, or recommendations..."
              rows={4}
            />
          </div>

          <div className="uat-documentation">
            <h4>Upload Test Documentation (Optional)</h4>
            <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
              <input {...getInputProps()} />
              {uatFile ? (
                <div className="file-info">
                  <p>{uatFile.name}</p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUatFile(null);
                    }}
                    className="remove-file"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={24} />
                  <p>Drop UAT documentation here or click to browse</p>
                  <p className="file-types">Supports: PDF, DOCX, XLSX, CSV</p>
                </>
              )}
            </div>
          </div>

          <div className={`uat-status ${testsPassed ? 'passed' : 'pending'}`}>
            {testsPassed ? (
              <>
                <Check size={20} />
                <span>Tests confirmed - ready to publish workflow</span>
              </>
            ) : (
              <>
                <X size={20} />
                <span>Please confirm that all tests have passed</span>
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!testsPassed || loading}
          >
            {loading ? 'Submitting...' : 'Complete UAT & Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};